import type { IDeprecationContext } from '~/types/deprecation'

import config from '~/config'

/**
 * Catalyst Cohorted Analytics tab (`/analytics/cohorted/:page`) of each legacy page (`/analytics/ca/:page`)
 */
const CATALYST_COHORTED_PAGES: Record<string, string> = {
  'app-usage': 'app-usage',
  community: 'community',
  favourites: 'favourites',
  'metrics-overview': 'metrics',
  'monet-ads': 'monet-ads',
  'monet-iap': 'monet-iap',
  overview: 'overview',
  'publishing-overview': 'publishing',
  sandbox: 'sandbox',
  stability: 'stability',
  usage: 'usage',
}

const getCatalystCohortedAnalyticsPath = (pathname: string): string => {
  if (pathname.startsWith('/compare-cohorts')) return '/analytics/cohorted/compare'

  const [legacyPage, chartConfigId] = pathname.replace(/^\/analytics\/ca\/?/, '').split('/')

  // NOTE: Catalyst edits the same chart configs, so the config id is kept
  if (legacyPage === 'builder') return `/analytics/chart-configs/builder${chartConfigId ? `/${chartConfigId}` : ''}`

  return `/analytics/cohorted/${CATALYST_COHORTED_PAGES[legacyPage] || 'overview'}`
}

/**
 * It returns the Catalyst page matching a legacy Cohorted Analytics page, with the selected app (`apps=key:platform`)
 */
export const getCatalystCohortedAnalyticsUrl = ({ appKey, deviceType, pathname }: IDeprecationContext): string => {
  const url = new URL(getCatalystCohortedAnalyticsPath(pathname), config.CATALYST_URL)

  if (appKey) url.searchParams.set('apps', `${appKey}:${deviceType}`)

  return url.toString()
}

/**
 * Catalyst Dynamic Screen page (`/dynamic-screen/:page`) of each legacy page (`/dynamic-screens/:page`)
 */
const CATALYST_DYNAMIC_SCREEN_PAGES: Record<string, string> = {
  arbitration: 'arbitration',
  'assets-management': 'assets',
  'navigation-graphs': 'navigation-graphs',
  'page-containers': 'page-containers',
  placements: 'placements',
  templates: 'templates',
}

/**
 * It returns the Catalyst page matching a legacy Dynamic Screen page; both use the same APIs, so the opened page
 * container, navigation graph or asset is kept (not the creation forms)
 */
export const getCatalystDynamicScreenUrl = ({ pathname }: IDeprecationContext): string => {
  const [legacyPage, id] = pathname.replace(/^\/dynamic-screens\/?/, '').split('/')

  const page = CATALYST_DYNAMIC_SCREEN_PAGES[legacyPage] || 'templates'

  return new URL(`/dynamic-screen/${page}${id && id !== 'creation' ? `/${id}` : ''}`, config.CATALYST_URL).toString()
}

/**
 * It returns the Catalyst Business Report, which opens on its first page
 */
export const getCatalystBusinessReportUrl = (): string =>
  new URL('/analytics/business-report', config.CATALYST_URL).toString()
